import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Menu,
  MenuItem,
  Alert,
  CircularProgress,
  ListItemIcon,
  ListItemText,
  Divider,
  Button,
  Stack,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import logo from "../../images/logoSimple.png";
import { Link } from "react-router-dom";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import PeopleIcon from "@mui/icons-material/People";
import TimelineIcon from "@mui/icons-material/Timeline";
import MessageIcon from "@mui/icons-material/Message";
import DescriptionIcon from "@mui/icons-material/Description";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import StoreIcon from "@mui/icons-material/Store";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import PersonIcon from "@mui/icons-material/Person";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import PaymentIcon from "@mui/icons-material/Payment";
import LogoutIcon from "@mui/icons-material/Logout";
import SwitchAccountIcon from "@mui/icons-material/SwitchAccount";
import { useUserInfo } from "../../utils/user";
import { useAuth } from "../../utils/auth";
import { useApi } from "../../utils/api";
import {
  getStripeBannerMessage,
  formatStripeFieldLabel,
} from "../../utils/stripe";
import NotificationsMenu from "./NotificationsMenu";
import MessageNotificationsButton from "./MessageNotificationsButton";

const PLATFORM_OWNER_USER_ID = import.meta.env.VITE_PLATFORM_OWNER_USER_ID;

const publicNavPaths = [
  "/",
  "/about",
  "/about-us",
  "/how-it-works",
  "/pricing",
];

const publicNavItems = [
  { to: "/how-it-works", label: "How It Works" },
  { to: "/pricing", label: "Pricing" },
];

const buildMenuItems = ({
  isCoach,
  stripeCustomerId,
  stripeIsSetup,
  paymentsProEnabled,
  stripeAccountId,
  showPaymentLink,
  handleClose,
}) => {
  if (isCoach) {
    const menuItems = [];

    // Manage Clients section
    menuItems.push({
      key: "header-clients",
      element: (
        <Box key="header-clients" sx={{ px: 2, py: 1 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: "text.secondary",
              textTransform: "uppercase",
            }}
          >
            Manage Clients
          </Typography>
        </Box>
      ),
    });
    menuItems.push({
      key: "/clients",
      element: (
        <MenuItem
          component={Link}
          to="/clients"
          onClick={handleClose}
          sx={{ width: "100%", display: "flex", alignItems: "center" }}
        >
          <ListItemIcon sx={{ minWidth: 40, color: "inherit" }}>
            <PeopleIcon />
          </ListItemIcon>
          <ListItemText primary="Clients" />
        </MenuItem>
      ),
    });
    menuItems.push({
      key: "/client-activity",
      element: (
        <MenuItem
          component={Link}
          to="/client-activity"
          onClick={handleClose}
          sx={{ width: "100%", display: "flex", alignItems: "center" }}
        >
          <ListItemIcon sx={{ minWidth: 40, color: "inherit" }}>
            <TimelineIcon />
          </ListItemIcon>
          <ListItemText primary="Client Activity" />
        </MenuItem>
      ),
    });
    menuItems.push({
      key: "/program-creator",
      element: (
        <MenuItem
          component={Link}
          to="/program-creator"
          onClick={handleClose}
          sx={{ width: "100%", display: "flex", alignItems: "center" }}
        >
          <ListItemIcon sx={{ minWidth: 40, color: "inherit" }}>
            <CalendarMonthIcon />
          </ListItemIcon>
          <ListItemText primary="Program Creator" />
        </MenuItem>
      ),
    });
    menuItems.push({
      key: "/messaging",
      element: (
        <MenuItem
          component={Link}
          to="/messaging"
          onClick={handleClose}
          sx={{ width: "100%", display: "flex", alignItems: "center" }}
        >
          <ListItemIcon sx={{ minWidth: 40, color: "inherit" }}>
            <MessageIcon />
          </ListItemIcon>
          <ListItemText primary="Messages" />
        </MenuItem>
      ),
    });

    // Manage Content section
    menuItems.push({
      key: "divider-content",
      element: <Divider key="divider-content" />,
    });
    menuItems.push({
      key: "header-content",
      element: (
        <Box key="header-content" sx={{ px: 2, py: 1 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: "text.secondary",
              textTransform: "uppercase",
            }}
          >
            Manage Content
          </Typography>
        </Box>
      ),
    });
    menuItems.push({
      key: "/templates",
      element: (
        <MenuItem
          component={Link}
          to="/templates"
          onClick={handleClose}
          sx={{ width: "100%", display: "flex", alignItems: "center" }}
        >
          <ListItemIcon sx={{ minWidth: 40, color: "inherit" }}>
            <DescriptionIcon />
          </ListItemIcon>
          <ListItemText primary="Programs & Templates" />
        </MenuItem>
      ),
    });
    menuItems.push({
      key: "/content-library",
      element: (
        <MenuItem
          component={Link}
          to="/content-library"
          onClick={handleClose}
          sx={{ width: "100%", display: "flex", alignItems: "center" }}
        >
          <ListItemIcon sx={{ minWidth: 40, color: "inherit" }}>
            <LibraryBooksIcon />
          </ListItemIcon>
          <ListItemText primary="Content Library" />
        </MenuItem>
      ),
    });
    menuItems.push({
      key: "/workout-import",
      element: (
        <MenuItem
          component={Link}
          to="/workout-import"
          onClick={handleClose}
          sx={{ width: "100%", display: "flex", alignItems: "center" }}
        >
          <ListItemIcon sx={{ minWidth: 40, color: "inherit" }}>
            <UploadFileIcon />
          </ListItemIcon>
          <ListItemText primary="Workout Import" />
        </MenuItem>
      ),
    });

    // Manage Business section (only when Stripe is setup AND Payments Pro enabled)
    const showManageBusiness = stripeIsSetup && paymentsProEnabled;
    if (showManageBusiness) {
      menuItems.push({
        key: "divider-business",
        element: <Divider key="divider-business" />,
      });
      menuItems.push({
        key: "header-business",
        element: (
          <Box key="header-business" sx={{ px: 2, py: 1 }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: "text.secondary",
                textTransform: "uppercase",
              }}
            >
              Manage Business
            </Typography>
          </Box>
        ),
      });
      menuItems.push({
        key: "/manage-products",
        element: (
          <MenuItem
            component={Link}
            to="/manage-products"
            onClick={handleClose}
            sx={{ width: "100%", display: "flex", alignItems: "center" }}
          >
            <ListItemIcon sx={{ minWidth: 40, color: "inherit" }}>
              <StoreIcon />
            </ListItemIcon>
            <ListItemText primary="Manage Products" />
          </MenuItem>
        ),
      });
      menuItems.push({
        key: "/payouts",
        element: (
          <MenuItem
            component={Link}
            to="/payouts"
            onClick={handleClose}
            sx={{ width: "100%", display: "flex", alignItems: "center" }}
          >
            <ListItemIcon sx={{ minWidth: 40, color: "inherit" }}>
              <AccountBalanceIcon />
            </ListItemIcon>
            <ListItemText primary="Payouts & Transactions" />
          </MenuItem>
        ),
      });
    }
    menuItems.push({
      key: "/coach-profile",
      element: (
        <MenuItem
          component={Link}
          to="/coach-profile"
          onClick={handleClose}
          sx={{ width: "100%", display: "flex", alignItems: "center" }}
        >
          <ListItemIcon sx={{ minWidth: 40, color: "inherit" }}>
            <PersonIcon />
          </ListItemIcon>
          <ListItemText primary="Coach Profile" />
        </MenuItem>
      ),
    });

    // Billing section (all coaches can access subscription)
    menuItems.push({
      key: "divider-billing",
      element: <Divider key="divider-billing" />,
    });
    menuItems.push({
      key: "header-billing",
      element: (
        <Box key="header-billing" sx={{ px: 2, py: 1 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: "text.secondary",
              textTransform: "uppercase",
            }}
          >
            Billing
          </Typography>
        </Box>
      ),
    });
    menuItems.push({
      key: "/coach-subscription",
      element: (
        <MenuItem
          component={Link}
          to="/coach-subscription"
          onClick={handleClose}
          sx={{ width: "100%", display: "flex", alignItems: "center" }}
        >
          <ListItemIcon sx={{ minWidth: 40, color: "inherit" }}>
            <CreditCardIcon />
          </ListItemIcon>
          <ListItemText primary="Subscription" />
        </MenuItem>
      ),
    });

    return menuItems;
  }

  return [
    { to: "/workouts", label: "Workouts", icon: <FitnessCenterIcon /> },
    { to: "/messaging", label: "Messages", icon: <MessageIcon /> },
    stripeCustomerId && {
      to: "/subscription",
      label: "Subscription",
      icon: <CreditCardIcon />,
    },
    (stripeIsSetup && !stripeCustomerId) || showPaymentLink
      ? {
          to: "/payment",
          label: "Payment",
          icon: <CreditCardIcon />,
        }
      : null,
  ]
    .filter(Boolean)
    .map((item) => ({
      key: item.to,
      element: (
        <MenuItem
          component={Link}
          to={item.to}
          onClick={handleClose}
          key={item.to}
          sx={{ width: "100%", display: "flex", alignItems: "center" }}
        >
          <ListItemIcon sx={{ minWidth: 40, color: "inherit" }}>
            {item.icon}
          </ListItemIcon>
          <ListItemText primary={item.label} />
        </MenuItem>
      ),
    }));
};

const Header = () => {
  const { user, signOut, impersonationClaims, signInWithCustomTokenAuth } =
    useAuth();
  const {
    leaveImpersonation,
    createAccount,
    updateUser,
    createAccountLink,
    getSubscription,
  } = useApi();
  const [anchorEl, setAnchorEl] = useState(null);
  const [leavingImpersonation, setLeavingImpersonation] = useState(false);
  const {
    isCoach,
    stripeCustomerId,
    stripeIsSetup,
    paymentsProEnabled,
    stripeAccountId,
    stripeDetailsSubmitted,
    stripeHasTasksDue,
    stripeDisabledReason,
    stripeCurrentlyDue = [],
    stripePendingVerification = [],
    loading,
    id,
    name,
    email,
    showPaymentLink,
  } = useUserInfo();
  const { pathname } = useLocation();
  const isPlatformUser = id === PLATFORM_OWNER_USER_ID;
  const open = Boolean(anchorEl);
  const isImpersonating = impersonationClaims?.isImpersonating;
  const showPublicNav = publicNavPaths.includes(pathname);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleLeaveImpersonation = async () => {
    if (leavingImpersonation) return;
    setLeavingImpersonation(true);
    try {
      const { customToken } = await leaveImpersonation();
      await signInWithCustomTokenAuth(customToken);
      localStorage.removeItem("userInfo");
      window.location.replace("/clients");
    } catch (err) {
      console.error("Failed to leave impersonation:", err);
      setLeavingImpersonation(false);
    }
  };

  const logoPath = isCoach ? "/clients" : stripeCustomerId ? "/workouts" : "/";
  const [stripeSetupLoading, setStripeSetupLoading] = useState(false);
  const [stripeSetupError, setStripeSetupError] = useState("");
  // Stripe setup phases: 1=no account, 2=onboarding incomplete, 3=tasks/restrictions, 4=all set (no banner)
  const stripePhase =
    !paymentsProEnabled || isPlatformUser
      ? 4
      : !stripeAccountId
      ? 1
      : !stripeDetailsSubmitted
      ? 2
      : stripeHasTasksDue || stripeDisabledReason
      ? 3
      : 4;
  const stripePhaseMessages = {
    1: "Connect to Stripe to enable payments. Create and link your account.",
    2: "Finish your Stripe onboarding to start collecting payments.",
    3: "You have tasks in Stripe to complete before accepting payments.",
  };
  const stripeBannerMessage = getStripeBannerMessage(
    stripeDisabledReason,
    stripePhase,
    stripePhaseMessages
  );
  const stripePhaseButtonText = {
    1: "Connect Stripe",
    2: "Finish onboarding",
    3: "Visit Stripe dashboard",
  };
  const handleStripeSetupClick = async () => {
    if (stripeSetupLoading) return;
    if (!stripeAccountId && !email) return;
    setStripeSetupLoading(true);
    setStripeSetupError("");
    try {
      let accountId = stripeAccountId;
      if (!accountId) {
        const response = await createAccount(email);
        if (!response?.account) {
          throw new Error(response?.error || "Failed to create Stripe account");
        }
        accountId = response.account;
        await updateUser({ stripeAccountId: accountId });
      }
      const accountLink = await createAccountLink(accountId);
      if (accountLink?.url) {
        window.location.href = accountLink.url;
      } else {
        throw new Error("Failed to get Stripe link");
      }
    } catch (err) {
      console.error("Error setting up Stripe account:", err);
      setStripeSetupError(
        err?.message || "Failed to set up Stripe. Please try again."
      );
    } finally {
      setStripeSetupLoading(false);
    }
  };
  const showStripeBanner =
    isCoach &&
    !loading &&
    stripePhase < 4 &&
    pathname !== "/coaching/create-account" &&
    pathname !== "/coaching/create-account-link" &&
    !pathname.startsWith("/coaching/return/") &&
    !pathname.startsWith("/coaching/refresh/");

  const [showStripeSuccessBanner, setShowStripeSuccessBanner] = useState(() =>
    typeof sessionStorage !== "undefined"
      ? sessionStorage.getItem("stripeOnboardingComplete") === "1"
      : false
  );
  useEffect(() => {
    if (showStripeSuccessBanner && typeof sessionStorage !== "undefined") {
      sessionStorage.removeItem("stripeOnboardingComplete");
    }
  }, [showStripeSuccessBanner]);
  const showStripeSuccess =
    showStripeSuccessBanner &&
    isCoach &&
    !loading &&
    stripePhase === 4 &&
    !showStripeBanner;
  const [clientSubscriptionStatus, setClientSubscriptionStatus] =
    useState(null);
  const [clientBannerDismissed, setClientBannerDismissed] = useState(false);
  useEffect(() => {
    let isMounted = true;
    const loadClientSubscription = async () => {
      if (loading || isCoach || !stripeCustomerId) {
        if (isMounted) setClientSubscriptionStatus(null);
        return;
      }
      try {
        const sub = await getSubscription(stripeCustomerId, stripeAccountId);
        if (isMounted) {
          setClientSubscriptionStatus(sub?.status || null);
        }
      } catch (err) {
        console.error("Failed to load client subscription status:", err);
      }
    };
    loadClientSubscription();
    return () => {
      isMounted = false;
    };
  }, [loading, isCoach, stripeCustomerId, stripeAccountId, getSubscription]);
  const showClientPaymentFailedBanner =
    !loading &&
    !isCoach &&
    !clientBannerDismissed &&
    ["past_due", "incomplete", "incomplete_expired", "unpaid"].includes(
      clientSubscriptionStatus
    );
  return (
    <Box sx={{ width: "100%", flexShrink: 0 }}>
      <AppBar
        position="static"
        sx={{
          bgcolor: "#fff",
          color: "#111",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Toolbar>
          <img src={logo} alt="Logo" width="30" />
          <Typography
            variant="h6"
            color="inherit"
            sx={{ ml: 1, "text-decoration": "none" }}
            component={Link}
            to={logoPath}
          >
            Trainer App
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          {showPublicNav && (
            <Stack
              direction="row"
              spacing={{ xs: 1.75, sm: 3.5 }}
              alignItems="center"
              sx={{ mr: { xs: 1.75, sm: 3.5 } }}
            >
              {publicNavItems.map((item) => (
                <Typography
                  key={item.to}
                  sx={{
                    color: "inherit",
                    textDecoration: "none",
                    fontSize: { xs: "0.82rem", sm: "1rem" },
                    fontWeight: pathname === item.to ? 700 : 400,
                  }}
                  component={Link}
                  to={item.to}
                >
                  {item.label}
                </Typography>
              ))}
            </Stack>
          )}
          {isImpersonating && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1.5,
                py: 0.5,
                mr: 1,
                borderRadius: 1,
                bgcolor: "rgba(255, 0, 0, 0.06)",
              }}
            >
              <SwitchAccountIcon sx={{ fontSize: 18 }} />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Viewing as {name || email || "user"}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={handleLeaveImpersonation}
                disabled={leavingImpersonation}
                sx={{
                  color: "inherit",
                  borderColor: "rgba(0,0,0,0.35)",
                  "&:hover": {
                    borderColor: "rgba(0,0,0,0.55)",
                    bgcolor: "rgba(0,0,0,0.06)",
                  },
                }}
              >
                {leavingImpersonation ? "Leaving…" : "Leave"}
              </Button>
            </Box>
          )}
          {loading ? (
            <IconButton>
              <CircularProgress size={28} sx={{ color: "inherit" }} />
            </IconButton>
          ) : (
            <>
              {!user ? (
                <Stack
                  direction="row"
                  spacing={{ xs: 1.75, sm: 3.5 }}
                  alignItems="center"
                >
                  <Typography
                    sx={{
                      color: "inherit",
                      textDecoration: "none",
                      fontSize: { xs: "0.82rem", sm: "1rem" },
                    }}
                    component={Link}
                    to="/login"
                  >
                    Login
                  </Typography>
                  <Typography
                    sx={{
                      color: "#ef0000",
                      textDecoration: "none",
                      fontSize: { xs: "0.82rem", sm: "1rem" },
                      fontWeight: 700,
                    }}
                    component={Link}
                    to="/coaching"
                  >
                    Start Free Trial
                  </Typography>
                </Stack>
              ) : (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.6,
                      color: "#000",
                    }}
                  >
                    <MessageNotificationsButton />
                    <NotificationsMenu />
                    <IconButton
                      id="basic-button"
                      aria-label="Open menu"
                      onClick={handleClick}
                      size="small"
                      sx={{ color: "inherit" }}
                    >
                      <MenuOutlinedIcon fontSize="medium" />
                    </IconButton>
                  </Box>
                  <Menu
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    MenuListProps={{
                      sx: {
                        py: 1,
                        "& .MuiMenuItem-root": {
                          py: 1.1,
                          minHeight: 44,
                        },
                        "& .MuiListItemIcon-root": {
                          minWidth: 44,
                        },
                      },
                    }}
                  >
                    {buildMenuItems({
                      isCoach,
                      stripeCustomerId,
                      stripeIsSetup,
                      paymentsProEnabled,
                      stripeAccountId,
                      showPaymentLink,
                      handleClose,
                    }).map((item) => item.element)}
                    {isPlatformUser && (
                      <>
                        <Divider />
                        <MenuItem
                          component={Link}
                          to="/admin"
                          onClick={handleClose}
                          sx={{
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 40, color: "inherit" }}>
                            <SwitchAccountIcon />
                          </ListItemIcon>
                          <ListItemText primary="Admin" />
                        </MenuItem>
                      </>
                    )}
                    <Divider />
                    <MenuItem
                      onClick={(e) => {
                        handleClose();
                        signOut();
                      }}
                      sx={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {user && (
                        <>
                          <ListItemIcon sx={{ minWidth: 40, color: "inherit" }}>
                            <LogoutIcon />
                          </ListItemIcon>
                          <ListItemText primary="Logout" />
                        </>
                      )}
                    </MenuItem>
                  </Menu>
                </>
              )}
            </>
          )}
        </Toolbar>
        {showStripeBanner && (
          <Alert
            severity={stripePhase === 1 ? "info" : "error"}
            sx={{ borderRadius: 0 }}
            action={
              <Button
                type="button"
                variant="outlined"
                size="small"
                onClick={handleStripeSetupClick}
                disabled={stripeSetupLoading}
              >
                {stripeSetupLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  stripePhaseButtonText[stripePhase]
                )}
              </Button>
            }
          >
            <>
              {stripeBannerMessage}
              {(stripeCurrentlyDue.length > 0 ||
                stripePendingVerification.length > 0) && (
                <Box sx={{ mt: 1 }}>
                  {stripeCurrentlyDue.length > 0 && (
                    <Box
                      sx={{ mb: stripePendingVerification.length ? 0.5 : 0 }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Required:
                      </Typography>
                      <Box
                        component="ul"
                        sx={{ m: 0, pl: 2.5, "& li": { mb: 0.25 } }}
                      >
                        {stripeCurrentlyDue.map((field) => (
                          <Typography
                            component="li"
                            variant="body2"
                            key={field}
                          >
                            {formatStripeFieldLabel(field)}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  )}
                  {stripePendingVerification.length > 0 && (
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Pending verification:
                      </Typography>
                      <Box
                        component="ul"
                        sx={{ m: 0, pl: 2.5, "& li": { mb: 0.25 } }}
                      >
                        {stripePendingVerification.map((field) => (
                          <Typography
                            component="li"
                            variant="body2"
                            key={field}
                          >
                            {formatStripeFieldLabel(field)}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
              {stripeSetupError && (
                <Typography
                  component="span"
                  variant="body2"
                  color="error"
                  sx={{ display: "block", mt: 1 }}
                >
                  {stripeSetupError}
                </Typography>
              )}
            </>
          </Alert>
        )}
        {showStripeSuccess && (
          <Alert
            severity="success"
            sx={{ borderRadius: 0 }}
            onClose={() => setShowStripeSuccessBanner(false)}
          >
            Stripe account set up successfully! You can start adding products to
            accept payments.
          </Alert>
        )}
        {showClientPaymentFailedBanner && (
          <Alert
            severity="error"
            sx={{ borderRadius: 0 }}
            onClose={() => setClientBannerDismissed(true)}
            action={
              <Button
                component={Link}
                to="/subscription"
                size="small"
                variant="outlined"
                color="inherit"
              >
                Manage Subscription
              </Button>
            }
          >
            A payment has failed. Open your subscription page to update your
            payment method and retry.
          </Alert>
        )}
      </AppBar>
    </Box>
  );
};

export default Header;
